import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const REMINDER_OPTIONS = [
  { value: 0, label: "No reminder" },
  { value: 1, label: "1 day before" },
  { value: 2, label: "2 days before" },
  { value: 3, label: "3 days before" },
  { value: 5, label: "5 days before" },
  { value: 7, label: "7 days before" },
];

const EditMeeting = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    title: "",
    description: "",
    meeting_date: "",
    meeting_time: "",
    reminder_days_before: 1,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  /* Load existing meeting from list */
  useEffect(() => {
    const load = async () => {
      dispatch(loadingTrue());
      const res = await Handler({
        method: "GET",
        url: `/meetings/list.php?page=1&limit=100`,
      });
      dispatch(loadingFalse());
      setLoading(false);
      if (res.success) {
        const found = (res.data?.list || []).find((m) => String(m.id) === String(id));
        if (found) {
          const dt = new Date(found.meeting_date);
          const pad = (n) => String(n).padStart(2, "0");
          setForm({
            title: found.title || "",
            description: found.description || "",
            meeting_date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
            meeting_time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
            reminder_days_before: found.reminder_days_before ?? 1,
          });
        } else {
          toast.error("Meeting not found");
          navigate("/user/meetings");
        }
      }
    };
    load();
  }, [id]);

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = "Title is required";
    else if (form.title.trim().length < 3) errs.title = "Minimum 3 characters";
    if (!form.meeting_date) errs.meeting_date = "Date is required";
    if (!form.meeting_time) errs.meeting_time = "Time is required";
    else {
      const dt = new Date(`${form.meeting_date}T${form.meeting_time}`);
      if (dt <= new Date()) errs.meeting_time = "Must be a future date & time";
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);

    const meeting_date = `${form.meeting_date} ${form.meeting_time}:00`;

    dispatch(loadingTrue());
    const res = await Handler({
      method: "POST",
      url: "/meetings/update.php",
      data: {
        meeting_id: parseInt(id),
        title: form.title,
        description: form.description,
        meeting_date,
        reminder_days_before: parseInt(form.reminder_days_before),
      },
    });
    dispatch(loadingFalse());

    if (res.success) {
      toast.success("Meeting updated!");
      navigate("/user/meetings");
    } else {
      toast.error(res.message || "Update failed");
    }
  };

  if (loading) {
    return (
      <div className="mt-page">
        <div className="mt-empty">
          <i className="bx bx-loader-alt bx-spin mt-empty-icon" />
          <p>Loading meeting...</p>
        </div>
      </div>
    );
  }

  const minDate = new Date().toISOString().split("T")[0];

  return (
    <MainLayout>
      <FeedLayout>
        <div className="mt-page">
          <button className="mt-btn-back" onClick={() => navigate("/user/meetings")}>
            <i className="bx bx-arrow-back" /> Back to Meetings
          </button>

          <div className="mt-header mt-2">
            <div>
              <h1 className="mt-title">Edit Meeting</h1>
              <p className="mt-subtitle">Update the meeting details below</p>
            </div>
          </div>

          <div className="mt-form-wrap">
            <form className="mt-form" onSubmit={handleSubmit}>

              <div className="mt-field">
                <label className="mt-label">Meeting Title <span className="mt-req">*</span></label>
                <input
                  className={`mt-input ${errors.title ? "mt-input-err" : ""}`}
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g. Q2 Strategy Discussion"
                />
                {errors.title && <span className="mt-err-msg"><i className="bx bx-error-circle" /> {errors.title}</span>}
              </div>

              <div className="mt-field">
                <label className="mt-label">Description</label>
                <textarea
                  className="mt-textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Agenda, participants, objectives..."
                  rows={4}
                />
              </div>

              <div className="mt-field-row">
                <div className="mt-field">
                  <label className="mt-label">Date <span className="mt-req">*</span></label>
                  <input
                    type="date"
                    className={`mt-input ${errors.meeting_date ? "mt-input-err" : ""}`}
                    name="meeting_date"
                    value={form.meeting_date}
                    onChange={handleChange}
                    min={minDate}
                  />
                  {errors.meeting_date && <span className="mt-err-msg"><i className="bx bx-error-circle" /> {errors.meeting_date}</span>}
                </div>

                <div className="mt-field">
                  <label className="mt-label">Time <span className="mt-req">*</span></label>
                  <input
                    type="time"
                    className={`mt-input ${errors.meeting_time ? "mt-input-err" : ""}`}
                    name="meeting_time"
                    value={form.meeting_time}
                    onChange={handleChange}
                  />
                  {errors.meeting_time && <span className="mt-err-msg"><i className="bx bx-error-circle" /> {errors.meeting_time}</span>}
                </div>
              </div>

              <div className="mt-field">
                <label className="mt-label">
                  <i className="bx bx-bell" style={{ color: "#2563eb", marginRight: 6 }} />
                  Reminder
                </label>
                <div className="mt-reminder-grid">
                  {REMINDER_OPTIONS.map((opt) => (
                    <label key={opt.value} className={`mt-reminder-chip-btn ${parseInt(form.reminder_days_before) === opt.value ? "active" : ""}`}>
                      <input
                        type="radio"
                        name="reminder_days_before"
                        value={opt.value}
                        checked={parseInt(form.reminder_days_before) === opt.value}
                        onChange={handleChange}
                        style={{ display: "none" }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-form-actions">
                <button type="button" className="mt-btn-outline" onClick={() => navigate("/user/meetings")}>
                  Cancel
                </button>
                <button type="submit" className="mt-btn-primary">
                  <i className="bx bx-save" /> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </FeedLayout>
    </MainLayout>

  );
};

export default EditMeeting;